import React from "react";
import {
  ShieldCheck,
  Activity,
  Factory,
  ArrowUpRight,
  CheckCircle2,
  Globe2,
  Droplets,
  Users2,
  Waves,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Linkedin } from "lucide-react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { assets } from "../data/siteData";
import PurposeSection from "./PurposeSection";

const pillars = [
  {
    title: "Engineering-Led Delivery",
    text: "We design water systems around real operating conditions, serviceability, durability, and site-specific performance.",
    icon: ShieldCheck,
  },
  {
    title: "Digital Visibility",
    text: "Our infrastructure can be paired with monitoring, dashboards, automation, and data-backed operational control.",
    icon: Activity,
  },
  {
    title: "Field-Ready Execution",
    text: "From treatment systems to dispensing, pumping, and wastewater solutions, we focus on systems that work in the real world.",
    icon: Factory,
  },
];

const values = [
  "We combine hardware, implementation, monitoring, and service support in one ecosystem.",
  "We build for long-term reliability, not short-term installation alone.",
  "We adapt solutions to local site conditions, water quality, and operating requirements.",
  "We believe water infrastructure should feel modern, intelligent, and dependable.",
];

const stats = [
  { value: "550+", label: "Installations & impact points", icon: Droplets },
  { value: "24/7", label: "Monitoring-ready approach", icon: Activity },
  { value: "Multi", label: "Sector water infrastructure capability", icon: Globe2 },
  { value: "Strong", label: "Field support orientation", icon: Users2 },
];

const leadership = [
  {
    name: "Rituparna Das",
    role: "CTO, Founder",
    image: assets.rituparna || assets.waterAtmCity,
    linkedin: "https://www.linkedin.com/in/rituparna-das-007a3717/",
  },
  {
    name: "Navin Gupta",
    role: "CEO, Founder",
    image: assets.navin || assets.waterAtmCity,
    linkedin: "https://www.linkedin.com/in/gnavin/",
  },
  {
    name: "Jyotsna Trivedi",
    role: "CMO,Founder",
    image: assets.jyotsna || assets.waterAtmCity,
    linkedin: "https://www.linkedin.com/in/jyotsna-trivedi-111a6935/",
  },
];

const focusAreas = [
  {
    title: "Water Access",
    text: "Smart dispensing, public access systems, and decentralized delivery models.",
    icon: Droplets,
  },
  {
    title: "Treatment Systems",
    text: "Integrated purification, filtration, and water quality-focused engineering.",
    icon: Waves,
  },
  {
    title: "Operations & Support",
    text: "Maintenance thinking, uptime focus, and service-backed deployments.",
    icon: CheckCircle2,
  },
];

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About Hydrotec Solutions | Water Treatment Company in Kolkata, West Bengal"
        description="Hydrotec Solutions is a Kolkata-based water treatment company providing water purification, pump systems, bacteria removal, and smart water infrastructure across Newtown, Rajarhat, and West Bengal. Award-winning water technology company."
        keywords="Hydrotec Solutions Kolkata, water treatment company West Bengal, water company Kolkata, water infrastructure Newtown, water purification company Rajarhat"
        canonical="https://hydroteclimited.com/about"
      />
      <PageHero
        title="We are the home of ambitious, passionate, and innovative water system builders."
        text="We engineer, deploy, and support modern water infrastructure designed for performance, visibility, and long-term trust."
        image={assets.six || assets.waterAtmCity}
        primaryText="Start a Project"
        primaryLink="/contact"
        secondaryText="Explore Solutions"
        secondaryLink="/solutions"
      />

      <section className="bg-white text-slate-950">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 sm:px-8 lg:grid-cols-[0.95fr_1.05fr] lg:px-10 lg:py-24">
          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-cyan-700">
              Overview
            </p>
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
              Hydrotec Solutions builds intelligent water infrastructure for a changing world.
            </h2>
          </div>

          <div className="space-y-6 text-lg leading-8 text-slate-600">
            <p>
              
              We work across water access, treatment, pumping, monitoring, wastewater,
              and connected infrastructure systems that combine engineering delivery
              with practical field execution.
            </p>
            <p>
              Our work is rooted in the belief that water systems should be reliable,
              visible, maintainable, and designed around real operational needs —
              not just technical specifications on paper. We carry out extensive technological 
              research to develop cutting-edge water solutions that are tailored to the unique 
              needs of our clients and the communities they serve.
            </p>
            <p>
              By combining physical infrastructure with digital readiness and strong
              deployment thinking, we help clients build water systems that are ready
              to perform over time. Hydrotec Solutions' leading water technology solutions 
              have been implemented across India and have won numerous awards.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:px-8 lg:px-10 lg:py-24">
          <div className="max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-white/55">
              Leadership
            </p>
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl">
              Leadership Team
            </h2>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {leadership.map((person) => (
              <div
                key={person.name}
                className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.04]"
              >
                <div className="h-72 overflow-hidden">
                  <img
                    src={person.image}
                    alt={person.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-semibold">{person.name}</h3>
                  <p className="mt-2 text-sm uppercase tracking-[0.28em] text-cyan-300">
                    {person.role}
                  </p>
                  {person.linkedin && (
                    <a
                      href={person.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-2 text-sm text-white/70 hover:text-cyan-300 transition"
                    >
                      <Linkedin className="h-4 w-4" />
                      View Profile
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PurposeSection />
      <section className="bg-slate-100 text-slate-950">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-20 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:px-10 lg:py-24">
          <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl">
            <img
              src={assets.cup}
              alt="Hydrotec infrastructure"
              className="h-full min-h-[420px] w-full object-cover"
            />
          </div>

          <div className="self-center">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.35em] text-cyan-700">
              Looking Ahead
            </p>
            <h2 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
              We aim to make water infrastructure more intelligent, more resilient, and more trusted.
            </h2>
            <p className="mt-6 text-lg leading-8 text-slate-600">
              Our ambition is to help shape a future where water systems are not
              fragmented or outdated, but connected, maintainable, scalable, and
              designed to deliver dependable service over time.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center rounded-full bg-slate-950 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Start a Project <ArrowUpRight className="ml-2 h-4 w-4" />
              </Link>

              <Link
                to="/solutions"
                className="inline-flex items-center rounded-full border border-slate-300 px-6 py-3.5 text-sm font-semibold text-slate-900 transition hover:border-slate-500"
              >
                Explore Solutions
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}